import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getTiposCambioInversiones,
    registrarTipoCambioInversiones,
    corregirTipoCambioInversiones
} from '../../services/adminApi';
import useYearSelector from '../../hooks/useYearSelector';
import YearSelector from '../../components/common/YearSelector';
import TablaTiposCambio from '../../components/InversionesExterior/TablaTiposCambio';
import { showError, showSuccess, showWarning } from '../../utils/alerts';
import '../../styles/pages/InversionesExterior/TiposCambio.css';

const formularioVacio = (anio) => ({ id: null, moneda: '', valor_clp: '', fecha_vigencia: `${anio}-12-31`, fuente: '' });

/**
 * Panel de EFE: tipos de cambio al 31-12 del módulo Inversiones exterior (D9)
 * Las tasas son comunes a todas las empresas. En la corrección no cambian el año
 * ni la moneda, y una tasa que usa alguna carga validada no se corrige hasta
 * reabrir esas cargas.
 */
const TiposCambioAdminPage = () => {
    const navigate = useNavigate();
    const { selectedYear, setSelectedYear, yearOptions } = useYearSelector(null, 2, 0);
    const [tasas, setTasas] = useState(null);
    const [form, setForm] = useState(formularioVacio(selectedYear));
    const [guardando, setGuardando] = useState(false);

    const buscar = useCallback(async () => {
        try {
            const respuesta = await getTiposCambioInversiones(selectedYear);
            setTasas(respuesta.data);
        } catch (error) {
            showError('No se pudieron obtener los tipos de cambio', error.response?.data?.message);
        }
    }, [selectedYear]);

    useEffect(() => {
        setTasas(null);
        setForm(formularioVacio(selectedYear));
        buscar();
    }, [buscar, selectedYear]);

    const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const handleCorregir = (tasa) => setForm({
        id: tasa.id,
        moneda: tasa.moneda,
        valor_clp: tasa.valor_clp,
        fecha_vigencia: tasa.fecha_vigencia,
        fuente: tasa.fuente
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setGuardando(true);
        const datos = { valor_clp: form.valor_clp, fecha_vigencia: form.fecha_vigencia, fuente: form.fuente };
        try {
            const respuesta = form.id
                ? await corregirTipoCambioInversiones(form.id, datos)
                : await registrarTipoCambioInversiones({ ...datos, anio_comercial: selectedYear, moneda: form.moneda });
            const { moneda, anio_comercial, filas_recalculadas: recalculadas } = respuesta.data;
            showSuccess(`Tasa ${moneda} de ${anio_comercial} ${form.id ? 'corregida' : 'registrada'}`
                + (recalculadas > 0 ? `. Se pasaron a pesos ${recalculadas} fila(s) de cargas por revisar.` : ''));
            setForm(formularioVacio(selectedYear));
            buscar();
        } catch (error) {
            const { message, cargas } = error.response?.data || {};
            if (cargas?.length) {
                const detalle = cargas.map((c) => `${c.empresa} (carga ${c.id_carga})`).join('; ');
                showWarning('No se puede corregir la tasa', `${message} Cargas: ${detalle}.`);
            } else {
                showError('No se guardó la tasa', message || 'Error al guardar el tipo de cambio');
            }
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="invext-tc-admin-page">
            <header className="admin-header">
                <div className="header-content">
                    <div className="header-left">
                        <button className="btn-back" onClick={() => navigate('/admin/clientes')}>← Volver</button>
                        <h1>Tipos de cambio · Inversiones exterior</h1>
                    </div>
                </div>
            </header>

            <div className="invext-tc-container">
                <div className="invext-tc-seccion">
                    <div className="invext-tc-anio">
                        <YearSelector
                            selectedYear={selectedYear}
                            onYearChange={setSelectedYear}
                            yearOptions={yearOptions}
                            label="Año de los movimientos"
                        />
                        <p className="invext-tc-ayuda">
                            Una tasa por moneda y año, común a todas las empresas. Los movimientos de {selectedYear} se
                            declaran en el AT {selectedYear + 1}.
                        </p>
                    </div>
                </div>

                <form className="invext-tc-seccion" onSubmit={handleSubmit}>
                    <h2>{form.id ? `Corregir la tasa ${form.moneda} de ${selectedYear}` : `Registrar una tasa de ${selectedYear}`}</h2>
                    <div className="invext-tc-campos">
                        <label>
                            Moneda
                            <input
                                name="moneda"
                                value={form.moneda}
                                onChange={handleChange}
                                placeholder="USD"
                                maxLength={3}
                                className="mayusculas"
                                disabled={!!form.id}
                                required
                            />
                        </label>
                        <label>
                            Pesos por unidad
                            <input
                                name="valor_clp"
                                value={form.valor_clp}
                                onChange={handleChange}
                                placeholder="950,25"
                                inputMode="decimal"
                                required
                            />
                        </label>
                        <label>
                            Fecha de vigencia
                            <input
                                type="date"
                                name="fecha_vigencia"
                                value={form.fecha_vigencia}
                                onChange={handleChange}
                                min={`${selectedYear}-01-01`}
                                max={`${selectedYear}-12-31`}
                                required
                            />
                        </label>
                        <label className="ancho">
                            Fuente
                            <input
                                name="fuente"
                                value={form.fuente}
                                onChange={handleChange}
                                placeholder="Dólar observado, Banco Central, Diario Oficial del 31-12"
                                maxLength={200}
                                required
                            />
                        </label>
                    </div>
                    <div className="invext-tc-acciones">
                        {form.id && (
                            <button type="button" className="invext-btn-sec" onClick={() => setForm(formularioVacio(selectedYear))}>
                                Cancelar
                            </button>
                        )}
                        <button type="submit" className="invext-btn" disabled={guardando}>
                            {guardando ? 'Guardando...' : form.id ? 'Corregir' : 'Registrar'}
                        </button>
                    </div>
                </form>

                <div className="invext-tc-seccion">
                    <h2>Tasas de {selectedYear}</h2>
                    {tasas === null ? null : tasas.length === 0 ? (
                        <p className="invext-tc-aviso">Todavía no hay tasas registradas para {selectedYear}.</p>
                    ) : (
                        <TablaTiposCambio tasas={tasas} onCorregir={handleCorregir} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TiposCambioAdminPage;
